import React from 'react';
import TradeChart from '../Charts/TradeChart';
import ImportExportTrend from '../Charts/ImportExportTrend';

const ImportExportTab = ({ showDetails, openDrillDown }) => {
  const tradingPartners = [
    { id: 'china', country: 'China', import: '42,500 T', export: '15,200 T', balance: -27300 },
    { id: 'australia', country: 'Australia', import: '28,200 T', export: '8,500 T', balance: -19700 },
    { id: 'germany', country: 'Germany', import: '12,800 T', export: '18,200 T', balance: 5400 },
    { id: 'usa', country: 'USA', import: '15,800 T', export: '12,500 T', balance: -3300 },
    { id: 'japan', country: 'Japan', import: '10,500 T', export: '14,800 T', balance: 4300 }
  ];

  const getBalanceStatus = (balance) => {
    if (balance > 0) return 'status-indicator status-high';
    if (balance < -10000) return 'status-indicator status-low';
    return 'status-indicator status-medium';
  };

  const formatBalance = (balance) => {
    if (balance > 0) return `+${balance.toLocaleString()}`;
    return balance.toLocaleString();
  };

  // Handle trade partner click
  const handleTradePartnerClick = (partner) => {
    if (openDrillDown) {
      const partnerData = {
        'china': {
          title: 'China Trade Analysis',
          icon: 'fas fa-globe-asia',
          iconColor: '#ff6384',
          category: 'Major Trading Partner',
          summary: 'China accounts for 31.1% of total trade, with significant trade deficit of 27,300 tons.',
          details: 'China remains the largest trading partner with heavy imports of manufactured goods and exports of raw materials.',
          tradeMetrics: [
            { label: 'Total Trade', value: '57,700 T', change: '+8.5%' },
            { label: 'Trade Share', value: '31.1%', change: '+2.3%' },
            { label: 'Trade Deficit', value: '27,300 T', change: '-5.2% narrowing' },
            { label: 'Growth Rate', value: '+12.5%', change: 'YoY' }
          ],
          importAnalysis: {
            'Total Imports': '42,500 T',
            'Primary Imports': 'Electronics, Machinery, Apparel',
            'Import Growth': '+10.2%',
            'Major Ports': 'Shanghai, Shenzhen, Ningbo'
          },
          exportAnalysis: {
            'Total Exports': '15,200 T',
            'Primary Exports': 'Iron Ore, Coal, Agricultural Products',
            'Export Growth': '+15.8%',
            'Key Markets': 'Eastern China, Industrial centers'
          },
          tradeBalance: {
            'Deficit Amount': '27,300 T',
            'Deficit as % of Trade': '47.3%',
            'Trend': 'Narrowing by 5.2% quarterly',
            'Impact': 'Currency pressure, industrial competitiveness'
          },
          commodityBreakdown: [
            { commodity: 'Electronics', import: '35%', export: '5%', balance: 'Large Deficit' },
            { commodity: 'Machinery', import: '28%', export: '8%', balance: 'Deficit' },
            { commodity: 'Iron Ore', import: '2%', export: '45%', balance: 'Surplus' },
            { commodity: 'Agricultural', import: '5%', export: '20%', balance: 'Surplus' }
          ],
          marketOpportunities: [
            'Increase high-value exports',
            'Diversify import sources',
            'Develop technology partnerships',
            'Leverage trade agreements'
          ],
          strategicInitiatives: [
            'China-focused export promotion program',
            'Quality certification for agricultural exports',
            'Direct shipping routes optimization',
            'Trade delegation exchanges'
          ],
          riskFactors: [
            'Geopolitical tensions',
            'Currency fluctuations',
            'Supply chain dependencies',
            'Trade policy changes'
          ],
          forecast: 'Expected trade growth of 8-10% in 2024 with narrowing deficit',
          confidence: 'Medium-High (strong economic ties)'
        },
        'germany': {
          title: 'Germany Trade Analysis',
          icon: 'fas fa-globe-europe',
          iconColor: '#4da6ff',
          category: 'Major Export Market',
          summary: 'Germany represents positive trade balance of 5,400 tons, primarily exporting manufactured goods.',
          details: 'Strong trade relationship with Germany focusing on high-value exports and technology exchange.',
          tradeMetrics: [
            { label: 'Total Trade', value: '31,000 T', change: '+12.3%' },
            { label: 'Trade Share', value: '16.7%', change: '+1.8%' },
            { label: 'Trade Surplus', value: '5,400 T', change: '+8.5% increase' },
            { label: 'Export Growth', value: '+18.2%', change: 'YoY' }
          ]
        }
      };
      
      openDrillDown('trade-partner', partnerData[partner.id] || {
        title: `${partner.country} Trade Analysis`,
        icon: 'fas fa-globe',
        iconColor: '#4da6ff',
        category: 'Trading Partner',
        summary: `Trade with ${partner.country} shows ${partner.balance > 0 ? 'surplus' : 'deficit'} of ${Math.abs(partner.balance).toLocaleString()} tons.`,
        details: `Detailed analysis of trade relationship with ${partner.country}.`,
        tradeMetrics: [
          { label: 'Imports', value: partner.import, change: 'Monthly' },
          { label: 'Exports', value: partner.export, change: 'Monthly' },
          { label: 'Balance', value: formatBalance(partner.balance), change: partner.balance > 0 ? 'Surplus' : 'Deficit' },
          { label: 'Total Trade', value: `${parseInt(partner.import) + parseInt(partner.export)} T`, change: 'Combined' }
        ]
      }, `${partner.country} Trade Analysis`);
    }
  };

  // Handle KPI card click
  const handleKPIClick = (kpiType) => {
    if (openDrillDown) {
      const kpis = {
        'import-analysis': {
          title: 'Import Analysis',
          icon: 'fas fa-download',
          iconColor: '#4da6ff',
          category: 'Trade Performance',
          summary: 'Total imports of 99,800 tons with 8.5% monthly growth, dominated by containerized goods.',
          details: 'Import composition shows strong consumer demand and manufacturing inputs driving growth.',
          keyMetrics: [
            { label: 'Total Imports', value: '99,800 T', change: '+8.5%' },
            { label: 'YoY Growth', value: '+12.3%', change: 'Annual' },
            { label: 'Import Share', value: '68%', change: 'of total trade' },
            { label: 'Value', value: '$850M', change: 'Monthly' }
          ],
          commodityBreakdown: [
            { commodity: 'Containerized', volume: '58,420 T', share: '58.5%', growth: '+10.2%' },
            { commodity: 'Dry Bulk', volume: '22,150 T', share: '22.2%', growth: '+6.8%' },
            { commodity: 'Liquid Bulk', volume: '12,360 T', share: '12.4%', growth: '-8.2%' },
            { commodity: 'Break Bulk', volume: '6,870 T', share: '6.9%', growth: '+5.2%' }
          ],
          topSourceCountries: [
            { country: 'China', share: '42.6%', volume: '42,500 T', trend: 'Increasing' },
            { country: 'Australia', share: '28.3%', volume: '28,200 T', trend: 'Stable' },
            { country: 'USA', share: '15.8%', volume: '15,800 T', trend: 'Increasing' },
            { country: 'Others', share: '13.3%', volume: '13,300 T', trend: 'Various' }
          ],
          importCategories: [
            { category: 'Consumer Goods', share: '35%', growth: '+15.2%', examples: 'Electronics, Apparel' },
            { category: 'Raw Materials', share: '28%', growth: '+8.5%', examples: 'Iron Ore, Coal' },
            { category: 'Machinery', share: '22%', growth: '+12.8%', examples: 'Industrial Equipment' },
            { category: 'Energy', share: '15%', growth: '-12.5%', examples: 'Crude Oil, LNG' }
          ],
          growthDrivers: [
            'Strong consumer demand post-pandemic',
            'Manufacturing recovery increasing input needs',
            'Infrastructure development projects',
            'Competitive global sourcing'
          ],
          riskFactors: [
            'Supply chain vulnerabilities',
            'Currency exchange fluctuations',
            'Geopolitical trade tensions',
            'Domestic industry competition'
          ],
          strategicRecommendations: [
            'Diversify import sources for risk mitigation',
            'Develop strategic stockpiles for critical materials',
            'Negotiate better trade terms with key partners',
            'Invest in import substitution where feasible'
          ],
          forecast: 'Expected import growth of 7-9% in 2024',
          confidence: 'High (based on current economic indicators)'
        },
        'export-analysis': {
          title: 'Export Analysis',
          icon: 'fas fa-upload',
          iconColor: '#4cd964',
          category: 'Trade Performance',
          summary: 'Total exports of 59,000 tons with 12.3% monthly growth, showing strong recovery.',
          details: 'Export growth driven by agricultural products and manufactured goods to key markets.',
          keyMetrics: [
            { label: 'Total Exports', value: '59,000 T', change: '+12.3%' },
            { label: 'YoY Growth', value: '+18.5%', change: 'Annual' },
            { label: 'Export Share', value: '32%', change: 'of total trade' },
            { label: 'Value', value: '$540M', change: 'Monthly' }
          ],
          commodityBreakdown: [
            { commodity: 'Containerized', volume: '27,000 T', share: '45.8%', growth: '+14.2%' },
            { commodity: 'Dry Bulk', volume: '20,000 T', share: '33.9%', growth: '+10.5%' },
            { commodity: 'Liquid Bulk', volume: '6,000 T', share: '10.2%', growth: '+3.8%' },
            { commodity: 'Break Bulk', volume: '6,000 T', share: '10.1%', growth: '+8.2%' }
          ],
          topDestinationCountries: [
            { country: 'Germany', share: '30.8%', volume: '18,200 T', trend: 'Strong Growth' },
            { country: 'Japan', share: '25.1%', volume: '14,800 T', trend: 'Stable' },
            { country: 'USA', share: '21.2%', volume: '12,500 T', trend: 'Increasing' },
            { country: 'Others', share: '22.9%', volume: '13,500 T', trend: 'Various' }
          ],
          exportCategories: [
            { category: 'Agricultural Products', share: '35%', growth: '+25.2%', examples: 'Grains, Fruits, Meat' },
            { category: 'Manufactured Goods', share: '30%', growth: '+18.5%', examples: 'Machinery, Equipment' },
            { category: 'Chemicals', share: '20%', growth: '+12.8%', examples: 'Fertilizers, Pharmaceuticals' },
            { category: 'Raw Materials', share: '15%', growth: '+8.5%', examples: 'Minerals, Metals' }
          ],
          competitiveAdvantages: [
            'High-quality agricultural products',
            'Competitive manufacturing costs',
            'Strategic geographic location',
            'Trade agreement benefits'
          ],
          marketOpportunities: [
            'Expand to new Asian markets',
            'Develop value-added exports',
            'Leverage free trade agreements',
            'Focus on sustainable products'
          ],
          exportPromotionInitiatives: [
            'Export credit guarantee scheme',
            'Market development assistance',
            'Quality certification programs',
            'Trade mission support'
          ],
          challenges: [
            'Logistics costs and infrastructure',
            'Non-tariff barriers in some markets',
            'Currency volatility',
            'Competition from other exporting nations'
          ],
          forecast: 'Expected export growth of 15-18% in 2024',
          confidence: 'Medium-High (dependent on global economic conditions)'
        }
      };
      openDrillDown('trade-kpi', kpis[kpiType], kpis[kpiType].title);
    }
  };

  // Handle commodity trade row click
  const handleCommodityTradeClick = (commodityType) => {
    if (openDrillDown) {
      const commodityData = {
        'containerized': {
          title: 'Containerized Trade Analysis',
          icon: 'fas fa-box',
          iconColor: '#4da6ff',
          category: 'Commodity Trade',
          summary: 'Containerized trade shows significant deficit with imports 2.2x higher than exports.',
          details: 'This reflects consumer goods imports exceeding manufactured exports, typical for developing economies.',
          tradeMetrics: [
            { label: 'Total Trade', value: '85,420 T', change: '+12.5%' },
            { label: 'Import Share', value: '68.4%', change: 'of containerized trade' },
            { label: 'Export Share', value: '31.6%', change: 'of containerized trade' },
            { label: 'Trade Deficit', value: '31,420 T', change: '-2.8% narrowing' }
          ],
          importDetails: {
            'Volume': '58,420 T',
            'Growth': '+10.2%',
            'Primary Goods': 'Electronics (32%), Apparel (28%), Machinery (22%)',
            'Source Markets': 'China (45%), Vietnam (18%), Malaysia (12%)'
          },
          exportDetails: {
            'Volume': '27,000 T',
            'Growth': '+18.5%',
            'Primary Goods': 'Agricultural Products (35%), Chemicals (28%), Manufactured Goods (22%)',
            'Destination Markets': 'Germany (30%), Japan (25%), USA (21%)'
          },
          marketAnalysis: 'Import-heavy pattern typical for consumer markets, export growth showing positive trend',
          strategicFocus: 'Increase value-added exports, reduce import dependency on single sources',
          forecast: 'Expected to reduce deficit by 5-7% annually through export promotion'
        }
      };
      
      openDrillDown('commodity-trade', commodityData[commodityType] || {
        title: `${commodityType} Trade Analysis`,
        icon: 'fas fa-exchange-alt',
        iconColor: '#4da6ff',
        category: 'Commodity Trade',
        summary: `Trade analysis for ${commodityType} commodities.`,
        details: 'Detailed trade performance and market analysis.',
        tradeMetrics: [
          { label: 'Import Volume', value: 'Data', change: 'Monthly' },
          { label: 'Export Volume', value: 'Data', change: 'Monthly' },
          { label: 'Trade Balance', value: 'Data', change: 'Monthly' }
        ]
      }, `${commodityType} Trade Analysis`);
    }
  };

  return (
    <div id="import-export-tab" className="tab-content-wrapper">
      {/* Insights Container */}
      <div className="insights-container">
        <div 
          className="insight-card clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Import Dominance',
            icon: 'fas fa-download',
            iconColor: '#4da6ff',
            category: 'Trade Pattern',
            summary: 'Imports account for 68% of total tonnage, with containerized goods leading at 42% of all imports.',
            details: 'This import-heavy pattern reflects strong domestic consumption and manufacturing input requirements.',
            keyMetrics: [
              { label: 'Import Share', value: '68%', change: '+2.3% from last year' },
              { label: 'Containerized Share', value: '42%', change: '+3.5%' },
              { label: 'Import Growth', value: '+8.5%', change: 'Monthly' },
              { label: 'Value of Imports', value: '$850M', change: 'Monthly' }
            ],
            importComposition: [
              { category: 'Consumer Goods', share: '35%', trend: 'Increasing', impact: 'Domestic consumption' },
              { category: 'Raw Materials', share: '28%', trend: 'Stable', impact: 'Manufacturing inputs' },
              { category: 'Capital Goods', share: '22%', trend: 'Growing', impact: 'Industrial investment' },
              { category: 'Energy Products', share: '15%', trend: 'Declining', impact: 'Energy transition' }
            ],
            economicImplications: [
              'Strong domestic demand supporting imports',
              'Manufacturing sector dependent on imported inputs',
              'Trade deficit affecting currency stability',
              'Opportunity for import substitution'
            ],
            sourceCountryAnalysis: [
              'China: 42.6% (electronics, machinery)',
              'Australia: 28.3% (raw materials, minerals)',
              'USA: 15.8% (technology, capital goods)',
              'Others: 13.3% (diversified sources)'
            ],
            strategicConsiderations: [
              'Diversify import sources to reduce risk',
              'Develop domestic manufacturing capabilities',
              'Negotiate favorable trade terms',
              'Implement strategic stockpiling'
            ],
            riskAssessment: [
              'Supply chain vulnerabilities',
              'Currency exchange volatility',
              'Geopolitical trade tensions',
              'Domestic industry competitiveness'
            ],
            policyRecommendations: [
              'Import substitution incentives',
              'Trade agreement optimization',
              'Strategic sourcing initiatives',
              'Domestic capacity building'
            ],
            forecast: 'Import growth expected to moderate to 6-8% in 2024',
            confidence: 'High (based on economic fundamentals)'
          }, 'Import Dominance Analysis')}
        >
          <div className="insight-title"><i className="fas fa-download"></i> Import Dominance</div>
          <div className="insight-content">Imports account for 68% of total tonnage, with containerized goods leading at 42% of all imports.</div>
        </div>
        
        <div 
          className="insight-card success clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Export Growth',
            icon: 'fas fa-upload',
            iconColor: '#4cd964',
            category: 'Trade Performance',
            summary: 'Exports grew by 15.2% this quarter, driven by increased agricultural and manufactured goods.',
            details: 'Strong export performance indicates improving competitiveness and market diversification.',
            keyMetrics: [
              { label: 'Export Growth', value: '+15.2%', change: 'Quarterly' },
              { label: 'YoY Growth', value: '+18.5%', change: 'Annual' },
              { label: 'Export Share', value: '32%', change: '+3.2%' },
              { label: 'Export Value', value: '$540M', change: 'Monthly' }
            ],
            growthDrivers: [
              'Agricultural exports increased by 25.2%',
              'Manufactured goods up 18.5%',
              'Chemical exports grew 12.8%',
              'New market penetration successful'
            ],
            destinationMarketAnalysis: [
              { market: 'Germany', share: '30.8%', growth: '+22.5%', products: 'Manufactured goods, Chemicals' },
              { market: 'Japan', share: '25.1%', growth: '+15.8%', products: 'Agricultural, Seafood' },
              { market: 'USA', share: '21.2%', growth: '+18.2%', products: 'Electronics, Automotive parts' },
              { market: 'Others', share: '22.9%', growth: '+12.5%', products: 'Diversified' }
            ],
            commodityPerformance: [
              { commodity: 'Agricultural Products', growth: '+25.2%', contribution: 'Leading growth sector' },
              { commodity: 'Manufactured Goods', growth: '+18.5%', contribution: 'Value-added exports' },
              { commodity: 'Chemicals', growth: '+12.8%', contribution: 'Specialized products' },
              { commodity: 'Raw Materials', growth: '+8.5%', contribution: 'Traditional strength' }
            ],
            competitiveAdvantages: [
              'Quality agricultural products',
              'Competitive manufacturing costs',
              'Strategic geographic location',
              'Skilled workforce'
            ],
            exportPromotionSuccesses: [
              'Trade mission to Europe generated $50M in contracts',
              'Quality certification accepted in 15 new markets',
              'Export credit support enabled SME expansion',
              'Market intelligence program identifying opportunities'
            ],
            challenges: [
              'Logistics infrastructure constraints',
              'Non-tariff barriers in some markets',
              'Currency volatility affecting competitiveness',
              'Competition from other exporting nations'
            ],
            strategicInitiatives: [
              'Develop export processing zones',
              'Enhance trade finance availability',
              'Strengthen market intelligence',
              'Focus on value-added exports'
            ],
            forecast: 'Expected to maintain 15-18% export growth in 2024',
            confidence: 'Medium-High (strong momentum observed)'
          }, 'Export Growth Analysis')}
        >
          <div className="insight-title"><i className="fas fa-upload"></i> Export Growth</div>
          <div className="insight-content">Exports grew by 15.2% this quarter, driven by increased agricultural and manufactured goods.</div>
        </div>
        
        <div 
          className="insight-card warning clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Trade Imbalance',
            icon: 'fas fa-balance-scale',
            iconColor: '#ff9f40',
            category: 'Trade Economics',
            summary: 'Trade imbalance at 36% (imports exceed exports). Focus needed on export promotion initiatives.',
            details: 'Persistent trade deficit indicates structural trade patterns requiring policy intervention and industrial strategy.',
            keyMetrics: [
              { label: 'Trade Deficit', value: '40,800 T', change: '-2.8% narrowing' },
              { label: 'Deficit as % of Trade', value: '36%', change: '-1.2% improvement' },
              { label: 'Monthly Deficit', value: '$310M', change: 'Currency impact' },
              { label: 'YoY Deficit Change', value: '-5.2%', change: 'Improving trend' }
            ],
            deficitAnalysis: [
              { factor: 'Consumer Goods Imports', contribution: '45%', trend: 'High', action: 'Import substitution' },
              { factor: 'Manufactured Exports', contribution: '35%', trend: 'Improving', action: 'Export promotion' },
              { factor: 'Energy Imports', contribution: '15%', trend: 'Declining', action: 'Energy diversification' },
              { factor: 'Services Trade', contribution: '5%', trend: 'Growing', action: 'Services export focus' }
            ],
            economicImpact: [
              'Currency pressure and foreign exchange reserves',
              'Industrial competitiveness challenges',
              'Employment implications in trade sectors',
              'Balance of payments considerations'
            ],
            structuralFactors: [
              'Domestic consumption patterns',
              'Manufacturing base development stage',
              'Natural resource endowment',
              'Global value chain participation'
            ],
            policyInterventions: [
              'Export promotion and incentives',
              'Import substitution industrialization',
              'Trade agreement optimization',
              'Industrial upgrading programs'
            ],
            successStories: [
              'Automotive parts exports increased by 35%',
              'Agricultural value addition improving margins',
              'IT services exports growing at 25% annually',
              'Renewable energy equipment manufacturing'
            ],
            targetAreas: [
              'Increase manufactured exports by 20%',
              'Reduce consumer goods import dependency',
              'Develop services exports',
              'Enhance regional trade integration'
            ],
            monitoringIndicators: [
              'Export/Import ratio improvement',
              'High-value export growth',
              'Import substitution progress',
              'Trade balance trajectory'
            ],
            forecast: 'Expected to reduce trade deficit to 30% of trade by 2025',
            confidence: 'Medium (requires sustained policy effort)'
          }, 'Trade Imbalance Analysis')}
        >
          <div className="insight-title"><i className="fas fa-balance-scale"></i> Trade Imbalance</div>
          <div className="insight-content">Trade imbalance at 36% (imports exceed exports). Focus needed on export promotion initiatives.</div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-cards" style={{marginTop: '30px'}}>
        <div 
          className="card clickable"
          onClick={() => handleKPIClick('import-analysis')}
        >
          <div className="card-header">
            <div className="card-title">Total Imports</div>
            <div className="card-icon" style={{backgroundColor: '#4da6ff'}}>
              <i className="fas fa-download"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">99,800 T</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>8.5% increase from last month</span>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => handleKPIClick('export-analysis')}
        >
          <div className="card-header">
            <div className="card-title">Total Exports</div>
            <div className="card-icon" style={{backgroundColor: '#4cd964'}}>
              <i className="fas fa-upload"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">59,000 T</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>12.3% increase from last month</span>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => openDrillDown && openDrillDown('trade-balance', {
            title: 'Trade Balance Analysis',
            icon: 'fas fa-balance-scale',
            iconColor: '#ff6384',
            category: 'Trade Economics',
            summary: 'Trade deficit of 40,800 tons, narrowing by 2.8% from last month.',
            details: 'Improving trend in trade balance indicates successful export promotion and import management strategies.',
            keyMetrics: [
              { label: 'Trade Deficit', value: '40,800 T', change: '-2.8% narrowing' },
              { label: 'Monthly Improvement', value: '1,150 T', change: 'Reduction' },
              { label: 'Deficit as % GDP', value: '2.3%', change: '-0.2% improvement' },
              { label: 'Annual Deficit', value: '$3.7B', change: '-5.2% reduction' }
            ],
            deficitComponents: [
              { component: 'Containerized Goods', deficit: '31,420 T', trend: 'Narrowing', contribution: '77%' },
              { component: 'Dry Bulk', deficit: '22,150 T', trend: 'Stable', contribution: '54%' },
              { component: 'Liquid Bulk', deficit: '16,360 T', trend: 'Widening', contribution: '40%' },
              { component: 'Break Bulk', surplus: '500 T', trend: 'Improving', contribution: '-1%' }
            ],
            improvementFactors: [
              'Export growth exceeding import growth',
              'Import substitution in some sectors',
              'Favorable commodity prices',
              'Currency management effectiveness'
            ],
            economicImplications: [
              'Reduced pressure on currency reserves',
              'Improved balance of payments',
              'Enhanced economic sovereignty',
              'Better debt sustainability metrics'
            ],
            policyEffectiveness: [
              'Export incentives showing results',
              'Import management strategies working',
              'Trade diversification successful',
              'Industrial policy supporting balance'
            ],
            targets: [
              'Reduce deficit to 30,000 T by end-2024',
              'Achieve trade balance in 3 key sectors',
              'Increase services trade surplus',
              'Improve terms of trade by 5%'
            ],
            monitoringMetrics: [
              'Monthly trade balance trajectory',
              'Export/Import growth differential',
              'Commodity price impacts',
              'Currency exchange effects'
            ],
            forecast: 'Expected to continue narrowing at 2-3% monthly rate',
            confidence: 'Medium-High (positive trend established)'
          }, 'Trade Balance Analysis')}
        >
          <div className="card-header">
            <div className="card-title">Trade Balance</div>
            <div className="card-icon" style={{backgroundColor: '#ff6384'}}>
              <i className="fas fa-balance-scale"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">-40,800 T</div>
            <div className="card-change negative">
              <i className="fas fa-arrow-down"></i>
              <span>Trade deficit narrowed by 2.8%</span>
            </div>
          </div>
        </div>
        
        <div 
          className="card clickable"
          onClick={() => openDrillDown && openDrillDown('trade-growth', {
            title: 'Trade Growth Rate Analysis',
            icon: 'fas fa-chart-line',
            iconColor: '#ff9f40',
            category: 'Trade Performance',
            summary: 'Overall trade growth of +10.2% YoY, with exports growing faster than imports.',
            details: 'Healthy trade expansion indicating economic vitality and global market integration.',
            keyMetrics: [
              { label: 'Total Trade Growth', value: '+10.2%', change: 'YoY' },
              { label: 'Export Growth', value: '+12.3%', change: 'Leading' },
              { label: 'Import Growth', value: '+8.5%', change: 'Moderate' },
              { label: 'Trade Volume', value: '158,800 T', change: 'Monthly total' }
            ],
            growthAnalysis: [
              { period: 'Current Month', total: '+10.2%', exports: '+12.3%', imports: '+8.5%' },
              { period: 'Quarterly Average', total: '+9.8%', exports: '+11.5%', imports: '+8.2%' },
              { period: 'Annual Trend', total: '+8.5%', exports: '+10.2%', imports: '+7.2%' }
            ],
            contributingFactors: [
              'Global economic recovery boosting demand',
              'Competitive export pricing',
              'Effective trade policies',
              'Infrastructure improvements'
            ],
            sectorPerformance: [
              { sector: 'Agriculture', exportGrowth: '+25.2%', importGrowth: '+5.8%' },
              { sector: 'Manufacturing', exportGrowth: '+18.5%', importGrowth: '+12.8%' },
              { sector: 'Mining', exportGrowth: '+8.5%', importGrowth: '+2.3%' },
              { sector: 'Services', exportGrowth: '+15.2%', importGrowth: '+10.5%' }
            ],
            regionalPerformance: [
              { region: 'Europe', growth: '+15.8%', share: '35%' },
              { region: 'Asia', growth: '+12.5%', share: '45%' },
              { region: 'Americas', growth: '+8.2%', share: '15%' },
              { region: 'Others', growth: '+5.5%', share: '5%' }
            ],
            sustainabilityIndicators: [
              'Export diversification index improving',
              'High-value export share increasing',
              'Trade partner diversification expanding',
              'Value-added content growing'
            ],
            growthQuality: [
              'Shift from volume to value growth',
              'Increasing technology content',
              'Better terms of trade',
              'Reduced commodity dependence'
            ],
            forecast: 'Expected to maintain 8-10% annual trade growth through 2024',
            confidence: 'High (strong fundamentals and momentum)'
          }, 'Trade Growth Analysis')}
        >
          <div className="card-header">
            <div className="card-title">Trade Growth Rate</div>
            <div className="card-icon" style={{backgroundColor: '#ff9f40'}}>
              <i className="fas fa-chart-line"></i>
            </div>
          </div>
          <div className="card-content">
            <div className="card-value">+10.2%</div>
            <div className="card-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>Overall trade growth YoY</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-chart-line"></i>
            <span>Import/Export Trend (Last 6 Months)</span>
          </h3>
          <div style={{height: '360px'}}>
            <ImportExportTrend 
              onClick={() => openDrillDown && openDrillDown('trade-trend', {
                title: 'Import/Export Trend Analysis',
                icon: 'fas fa-chart-line',
                iconColor: '#4da6ff',
                category: 'Trend Analysis',
                summary: 'Exports growing faster than imports over past 6 months, indicating improving trade balance.',
                details: 'Trend analysis shows positive momentum in export performance while import growth moderates.',
                trendData: [
                  { month: 'Nov 2023', imports: '92,500 T', exports: '52,800 T', balance: '-39,700 T' },
                  { month: 'Dec 2023', imports: '95,200 T', exports: '55,100 T', balance: '-40,100 T' },
                  { month: 'Jan 2024', imports: '96,800 T', exports: '56,500 T', balance: '-40,300 T' },
                  { month: 'Feb 2024', imports: '98,200 T', exports: '57,800 T', balance: '-40,400 T' },
                  { month: 'Mar 2024', imports: '99,100 T', exports: '58,600 T', balance: '-40,500 T' },
                  { month: 'Apr 2024', imports: '99,800 T', exports: '59,000 T', balance: '-40,800 T' }
                ],
                keyObservations: [
                  'Exports grew 11.8% over 6 months',
                  'Imports grew 7.9% over 6 months',
                  'Trade deficit peaked in March',
                  'Recent months show improvement'
                ],
                seasonalPatterns: [
                  'Q4 typically stronger for exports',
                  'Import surge in Q1 for inventory',
                  'Steady growth trend established',
                  'Reduced volatility compared to previous year'
                ],
                momentumIndicators: [
                  'Export growth accelerating',
                  'Import growth stabilizing',
                  'Balance improvement trend emerging',
                  'Consistent monthly performance'
                ],
                forecastImplications: [
                  'Positive trend expected to continue',
                  'Trade balance likely to improve further',
                  'Export momentum sustainable',
                  'Import growth to remain moderate'
                ],
                strategicInsights: [
                  'Export promotion efforts effective',
                  'Import management successful',
                  'Market diversification working',
                  'Competitiveness improving'
                ]
              }, 'Trade Trend Analysis')}
            />
          </div>
        </div>
        
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-exchange-alt"></i>
            <span>Import vs Export by Commodity</span>
          </h3>
          <div style={{height: '360px'}}>
            <TradeChart 
              onClick={() => openDrillDown && openDrillDown('commodity-trade-comparison', {
                title: 'Commodity Trade Comparison',
                icon: 'fas fa-exchange-alt',
                iconColor: '#4da6ff',
                category: 'Commodity Analysis',
                summary: 'Containerized trade shows largest deficit while break bulk shows trade surplus.',
                details: 'Commodity-level analysis reveals structural trade patterns and opportunities for balance improvement.',
                commodityAnalysis: [
                  { 
                    commodity: 'Containerized', 
                    importShare: '68.4%', 
                    exportShare: '31.6%', 
                    balance: 'Deficit', 
                    trend: 'Narrowing',
                    opportunities: 'Increase manufactured exports, diversify import sources'
                  },
                  { 
                    commodity: 'Dry Bulk', 
                    importShare: '67.8%', 
                    exportShare: '32.2%', 
                    balance: 'Deficit', 
                    trend: 'Stable',
                    opportunities: 'Develop value-added processing, explore new markets'
                  },
                  { 
                    commodity: 'Liquid Bulk', 
                    importShare: '78.8%', 
                    exportShare: '21.2%', 
                    balance: 'Deficit', 
                    trend: 'Widening',
                    opportunities: 'Diversify energy sources, develop LNG exports'
                  },
                  { 
                    commodity: 'Break Bulk', 
                    importShare: '47.4%', 
                    exportShare: '52.6%', 
                    balance: 'Surplus', 
                    trend: 'Improving',
                    opportunities: 'Leverage expertise, expand project cargo'
                  }
                ],
                strategicImplications: [
                  'Focus export promotion on deficit commodities',
                  'Develop import substitution in key sectors',
                  'Leverage surplus commodities for trade negotiations',
                  'Balance commodity mix for trade stability'
                ],
                policyRecommendations: [
                  'Targeted export incentives for deficit sectors',
                  'Import management for strategic goods',
                  'Investment in value-added processing',
                  'Trade agreement focus on balanced outcomes'
                ],
                performanceMetrics: [
                  'Overall trade balance improvement target: 15% reduction in deficit',
                  'Export growth target by commodity',
                  'Import substitution goals',
                  'Market diversification objectives'
                ]
              }, 'Commodity Trade Comparison')}
            />
          </div>
        </div>
        
        {/* Import/Export by Commodity Table */}
        <div className="table-container">
          <h3 className="section-title">
            <i className="fas fa-table"></i>
            <span>Import/Export Tonnage by Commodity</span>
          </h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Commodity</th>
                <th>Import (Tons)</th>
                <th>Export (Tons)</th>
                <th>Total</th>
                <th>Import %</th>
                <th>Export %</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                className="clickable-row"
                onClick={() => handleCommodityTradeClick('containerized')}
              >
                <td><strong>Containerized</strong></td>
                <td>58,420</td>
                <td>27,000</td>
                <td>85,420</td>
                <td>68.4%</td>
                <td>31.6%</td>
                <td><span className="status-indicator status-low">-31,420</span></td>
              </tr>
              <tr 
                className="clickable-row"
                onClick={() => openDrillDown && openDrillDown('commodity-trade', {
                  title: 'Dry Bulk Trade Analysis',
                  icon: 'fas fa-mountain',
                  iconColor: '#ff9f40',
                  category: 'Commodity Trade',
                  summary: 'Dry bulk trade deficit of 22,150 tons, primarily in raw material imports.',
                  details: 'Raw material imports for manufacturing exceed processed material exports.',
                  tradeMetrics: [
                    { label: 'Total Trade', value: '62,150 T', change: '+8.2%' },
                    { label: 'Import Share', value: '67.8%', change: 'of dry bulk trade' },
                    { label: 'Export Share', value: '32.2%', change: 'of dry bulk trade' },
                    { label: 'Trade Deficit', value: '22,150 T', change: 'Stable' }
                  ]
                }, 'Dry Bulk Trade Analysis')}
              >
                <td><strong>Dry Bulk</strong></td>
                <td>42,150</td>
                <td>20,000</td>
                <td>62,150</td>
                <td>67.8%</td>
                <td>32.2%</td>
                <td><span className="status-indicator status-low">-22,150</span></td>
              </tr>
              <tr 
                className="clickable-row"
                onClick={() => openDrillDown && openDrillDown('commodity-trade', {
                  title: 'Liquid Bulk Trade Analysis',
                  icon: 'fas fa-oil-can',
                  iconColor: '#ff6384',
                  category: 'Commodity Trade',
                  summary: 'Liquid bulk shows largest deficit proportion at 78.8% import share.',
                  details: 'Energy imports dominate while chemical exports show growth potential.',
                  tradeMetrics: [
                    { label: 'Total Trade', value: '28,360 T', change: '-8.2%' },
                    { label: 'Import Share', value: '78.8%', change: 'of liquid bulk trade' },
                    { label: 'Export Share', value: '21.2%', change: 'of liquid bulk trade' },
                    { label: 'Trade Deficit', value: '16,360 T', change: 'Widening' }
                  ]
                }, 'Liquid Bulk Trade Analysis')}
              >
                <td><strong>Liquid Bulk</strong></td>
                <td>22,360</td>
                <td>6,000</td>
                <td>28,360</td>
                <td>78.8%</td>
                <td>21.2%</td>
                <td><span className="status-indicator status-low">-16,360</span></td>
              </tr>
              <tr 
                className="clickable-row"
                onClick={() => openDrillDown && openDrillDown('commodity-trade', {
                  title: 'Break Bulk Trade Analysis',
                  icon: 'fas fa-cubes',
                  iconColor: '#4cd964',
                  category: 'Commodity Trade',
                  summary: 'Break bulk shows trade surplus of 500 tons, unique among commodity categories.',
                  details: 'Specialized project cargo and equipment exports exceed imports.',
                  tradeMetrics: [
                    { label: 'Total Trade', value: '9,500 T', change: '+5.2%' },
                    { label: 'Import Share', value: '47.4%', change: 'of break bulk trade' },
                    { label: 'Export Share', value: '52.6%', change: 'of break bulk trade' },
                    { label: 'Trade Surplus', value: '500 T', change: '+8.5% increase' }
                  ]
                }, 'Break Bulk Trade Analysis')}
              >
                <td><strong>Break Bulk</strong></td>
                <td>4,500</td>
                <td>5,000</td>
                <td>9,500</td>
                <td>47.4%</td>
                <td>52.6%</td>
                <td><span className="status-indicator status-high">+500</span></td>
              </tr>
              <tr className="highlight">
                <td><strong>Total</strong></td>
                <td>127,430</td>
                <td>58,000</td>
                <td>185,430</td>
                <td>68.7%</td>
                <td>31.3%</td>
                <td><span className="status-indicator status-low">-69,430</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Trading Partners Table */}
        <div className="table-container">
          <h3 className="section-title">
            <i className="fas fa-globe"></i>
            <span>Top Trading Partners</span>
          </h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Import</th>
                <th>Export</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {tradingPartners.map((partner, index) => (
                <tr 
                  key={index}
                  className="clickable-row"
                  onClick={() => handleTradePartnerClick(partner)}
                >
                  <td><strong>{partner.country}</strong></td>
                  <td>{partner.import}</td>
                  <td>{partner.export}</td>
                  <td><span className={getBalanceStatus(partner.balance)}>{formatBalance(partner.balance)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ImportExportTab;